import { Link } from "react-router-dom";
import PortalLayout from "@/layouts/PortalLayout";
import { useAdminTable } from "@/lib/useAdminTable";
import { AdminTableToolbar, AdminTablePager, SortHeader } from "@/components/admin/AdminTableHelpers";
import { api, formatApiErrorDetail } from "@/lib/api";
import { POLES, poleLabel } from "@/lib/poles";
import CompanyAutocomplete from "@/components/CompanyAutocomplete";
import CompanyLogo from "@/components/CompanyLogo";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { MapPin, Archive, ArchiveRestore, Trash2, Building2, Plus, X, Sparkles, Users2, Edit3, Tag as TagIcon, Shuffle, Download, Loader2 } from "lucide-react";
import { BulkActionsBar, BulkCheckbox } from "@/components/admin/BulkActionsBar";
import VisualDownloader from "@/components/VisualDownloader";
import CountrySelect from "@/components/CountrySelect";
import { langForCountry } from "@/lib/countries";

export default function AdminMembers() {
  const table = useAdminTable("/admin/companies", { sort: "created_at", order: "desc" });
  const [categories, setCategories] = useState([]);
  const [creating, setCreating] = useState(false);
  const [dupGroups, setDupGroups] = useState(null);
  const [dupOpen, setDupOpen] = useState(false);
  const [exportingVisuals, setExportingVisuals] = useState(false);
  useEffect(() => { api.get("/admin/settings/categories").then((r) => setCategories(r.data || [])).catch(() => {}); }, []);

  const exportVisuals = async () => {
    if (!window.confirm("Télécharger un ZIP de tous les logos sociétés + photos de profil membres ?\n\nLes fichiers seront renommés automatiquement : [nom-societe]-logo.jpg, [nom-membre]-photo.jpg.")) return;
    setExportingVisuals(true);
    try {
      const r = await api.get("/admin/export/visuals.zip", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `lawinetech-visuals-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("ZIP téléchargé ✅");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'export");
    } finally {
      setExportingVisuals(false);
    }
  };

  const archive = async (c) => {
    if (!window.confirm(`Archiver "${c.name}" ?`)) return;
    await api.post(`/admin/companies/${c.id}/archive`); toast.success("Membre archivé"); table.reload();
  };
  const unarchive = async (c) => {
    await api.post(`/admin/companies/${c.id}/unarchive`); toast.success("Membre restauré"); table.reload();
  };
  const remove = async (c) => {
    if (!window.confirm(`⚠️ SUPPRIMER DÉFINITIVEMENT "${c.name}" ?\nCette action est IRRÉVERSIBLE.\n\nUtilisez "Archiver" pour juste le masquer.`)) return;
    await api.delete(`/admin/companies/${c.id}`); toast.success("Supprimé"); table.reload();
  };

  // Bulk selection
  const [selected, setSelected] = useState(new Set());
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [fieldsModalOpen, setFieldsModalOpen] = useState(false);
  const [existingTags, setExistingTags] = useState([]);
  useEffect(() => { api.get("/admin/companies/tags").then((r) => setExistingTags(r.data?.tags || [])).catch(() => {}); }, []);

  // Sprint 92al — Preload projects catalog once to label each row's project_ids.
  const [projectsCatalog, setProjectsCatalog] = useState([]);
  useEffect(() => {
    api.get("/admin/settings/projects").then((r) => setProjectsCatalog(r.data || [])).catch(() => {});
  }, []);
  const projectById = (pid) => projectsCatalog.find((p) => p.id === pid);
  const toggleSel = (id) => { const ns = new Set(selected); if (ns.has(id)) ns.delete(id); else ns.add(id); setSelected(ns); };
  const selectAll = () => setSelected(new Set(table.items.map((c) => c.id)));
  const clearSel = () => setSelected(new Set());
  const bulkDelete = async () => {
    if (!window.confirm(`⚠️ SUPPRIMER DÉFINITIVEMENT ${selected.size} membre${selected.size > 1 ? "s" : ""} ?\nCette action est IRRÉVERSIBLE.\n\nUtilisez l'archive pour seulement masquer.`)) return;
    try {
      const r = await api.post("/admin/companies/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${r.data.deleted} membre${r.data.deleted > 1 ? "s" : ""} supprimé${r.data.deleted > 1 ? "s" : ""}`);
      clearSel();
      table.reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
  };

  const loadDuplicates = async () => {
    try {
      const { data } = await api.get("/admin/company-duplicates");
      setDupGroups(data);
      setDupOpen(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
  };

  const mergeDuplicates = async (group, primaryId) => {
    const dupIds = group.companies.map((c) => c.id).filter((id) => id !== primaryId);
    if (dupIds.length === 0) return;
    if (!window.confirm(
      `Fusionner ${dupIds.length} doublon(s) dans "${group.companies.find((c) => c.id === primaryId)?.name}" ?\n\n` +
      "Les inscriptions événement, contacts, news et candidatures seront rattachés à la fiche principale.\n" +
      "Les doublons seront archivés (statut « merged ») avec audit."
    )) return;
    try {
      const r = await api.post("/admin/company-merge", { primary_id: primaryId, duplicate_ids: dupIds });
      const mig = r.data?.migrations || {};
      const total = Object.values(mig).filter((v) => typeof v === "number").reduce((a, b) => a + b, 0);
      toast.success(`${dupIds.length} doublon(s) fusionné(s) · ${total} référence(s) migrée(s)`);
      loadDuplicates();
      table.reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
  };

  return (
    <PortalLayout sidebar="admin">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-navy md:text-4xl">Membres</h1>
            <p className="text-sm text-neutral-600">{table.total} entreprise{table.total > 1 ? "s" : ""} membre{table.total > 1 ? "s" : ""}.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={exportVisuals}
              disabled={exportingVisuals}
              className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-4 py-2 text-xs font-bold text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
              title="Télécharger un ZIP de tous les logos sociétés + photos de profil des membres (fichiers renommés automatiquement)"
              data-testid="export-visuals-btn"
            >
              {exportingVisuals ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              {exportingVisuals ? "Export en cours…" : "Exporter visuels"}
            </button>
            <button onClick={loadDuplicates} className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-4 py-2 text-xs font-bold text-amber-800 hover:bg-amber-100" data-testid="find-company-duplicates-btn">
              <Users2 className="h-3.5 w-3.5" /> Doublons
            </button>
            <button onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-4 py-2 text-xs font-bold text-white hover:bg-wine-700" data-testid="new-member-btn">
              <Plus className="h-3.5 w-3.5" /> Nouveau membre
            </button>
          </div>
        </div>

        <AdminTableToolbar
          table={table}
          placeholder="Rechercher nom, ville, pays…"
          filters={[
            { key: "pole", label: "Tous pôles", options: POLES.map((p) => ({ value: p.id, label: p.label })) },
            { key: "category", label: "Toutes catégories", options: categories.map((c) => ({ value: c.id, label: c.name })) },
            { key: "status", label: "Tous statuts", options: [{ value: "active", label: "Actif" }, { value: "inactive", label: "Inactif" }, { value: "pending", label: "En attente" }] },
            { key: "tag", label: "Tous tags", options: existingTags.map((t) => ({ value: t, label: t })) },
          ]}
        />

        {selected.size > 0 && (
          <BulkActionsBar
            count={selected.size}
            total={table.items.length}
            onClear={clearSel}
            onSelectAll={selectAll}
            onDelete={bulkDelete}
            label="membre"
            testId="admin-members-bulk"
            extraActions={
              <>
                <button
                  onClick={() => setTagModalOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600 px-3.5 py-1.5 text-xs font-bold text-white hover:bg-indigo-700"
                  data-testid="admin-members-bulk-tag"
                >
                  <TagIcon className="h-3.5 w-3.5" /> Ajouter/retirer un tag
                </button>
                <button
                  onClick={() => setFieldsModalOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-3.5 py-1.5 text-xs font-bold text-white hover:bg-wine-700"
                  data-testid="admin-members-bulk-fields"
                >
                  <Shuffle className="h-3.5 w-3.5" /> Pôle / catégorie
                </button>
              </>
            }
          />
        )}

        {tagModalOpen && (
          <BulkTagModal
            ids={Array.from(selected)}
            existingTags={existingTags}
            onClose={() => setTagModalOpen(false)}
            onDone={() => {
              setTagModalOpen(false);
              clearSel();
              table.reload();
              api.get("/admin/companies/tags").then((r) => setExistingTags(r.data?.tags || [])).catch(() => {});
            }}
          />
        )}

        {fieldsModalOpen && (
          <BulkFieldsModal
            ids={Array.from(selected)}
            categories={categories}
            onClose={() => setFieldsModalOpen(false)}
            onDone={() => {
              setFieldsModalOpen(false);
              clearSel();
              table.reload();
            }}
          />
        )}

        <div className="overflow-hidden rounded-2xl border border-neutral-100 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-wine-50/40 text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="px-3 py-3 w-8"><BulkCheckbox checked={table.items.length > 0 && selected.size === table.items.length} onChange={(v) => v ? selectAll() : clearSel()} testId="members-select-all" /></th>
                  <th className="px-4 py-3 text-left"><SortHeader table={table} col="name" label="Membre" /></th>
                  <th className="px-4 py-3 text-left">Pôle</th>
                  <th className="px-4 py-3 text-left">Catégorie</th>
                  <th className="px-4 py-3 text-left">Projets</th>
                  <th className="px-4 py-3 text-left">Statut</th>
                  <th className="px-4 py-3 text-left"><SortHeader table={table} col="created_at" label="Inscrit le" /></th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {table.items.map((c) => {
                  const cat = categories.find((x) => x.id === c.category);
                  return (
                    <tr key={c.id} className={`border-t border-neutral-100 hover:bg-wine-50/20 ${selected.has(c.id) ? "bg-rose-50/40" : ""}`} data-testid={`admin-member-${c.id}`}>
                      <td className="px-3 py-3"><BulkCheckbox checked={selected.has(c.id)} onChange={() => toggleSel(c.id)} testId={`member-select-${c.id}`} /></td>
                      <td className="px-4 py-3">
                        <Link to={`/admin/companies/${c.id}`} className="flex items-center gap-3">
                          <CompanyLogo src={c.logo} name={c.name} size={36} className="rounded-lg" />
                          <div>
                            <div className="font-semibold text-neutral-900 hover:text-wine-700">{c.name}</div>
                            {c.city && <div className="text-xs text-neutral-500"><MapPin className="inline h-3 w-3" /> {c.city}{c.country ? `, ${c.country}` : ""}</div>}
                            {Array.isArray(c.tags) && c.tags.length > 0 && (
                              <div className="mt-1 flex max-w-[240px] flex-wrap gap-0.5" data-testid={`member-tags-${c.id}`}>
                                {c.tags.slice(0, 4).map((t) => (
                                  <span key={t} className="rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700">{t}</span>
                                ))}
                                {c.tags.length > 4 && <span className="rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] text-neutral-500" title={c.tags.slice(4).join(", ")}>+{c.tags.length - 4}</span>}
                              </div>
                            )}
                          </div>
                        </Link>
                      </td>
                      <td className="px-4 py-3">{c.pole && <span className="rounded-full bg-wine-50 px-2 py-0.5 text-xs font-semibold text-wine-700">{poleLabel(c.pole)}</span>}</td>
                      <td className="px-4 py-3 text-xs text-neutral-700">{cat?.name || c.category || "—"}</td>
                      <td className="px-4 py-3" data-testid={`member-projects-${c.id}`}>
                        {Array.isArray(c.project_ids) && c.project_ids.length > 0 ? (
                          <div className="flex max-w-[200px] flex-wrap gap-0.5">
                            {c.project_ids.slice(0, 2).map((pid) => {
                              const p = projectById(pid);
                              return p ? <span key={pid} className="truncate rounded-full bg-blush-100 px-1.5 py-0.5 text-[10px] font-semibold text-wine-700" title={p.title}>🚀 {p.title}</span> : null;
                            })}
                            {c.project_ids.length > 2 && (
                              <span className="rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] text-neutral-500" title={c.project_ids.slice(2).map((pid) => (projectById(pid) || {}).title || pid).join(", ")}>+{c.project_ids.length - 2}</span>
                            )}
                          </div>
                        ) : <span className="text-xs text-neutral-300">—</span>}
                      </td>
                      <td className="px-4 py-3">{c.status && <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${c.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-neutral-100 text-neutral-600"}`}>{c.status}</span>}</td>
                      <td className="px-4 py-3 text-xs text-neutral-500">{c.created_at ? new Date(c.created_at).toLocaleDateString("fr") : "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Link to={`/admin/companies/${c.id}`} className="grid h-7 w-7 place-items-center rounded-lg text-neutral-500 hover:bg-blue-50 hover:text-blue-700" title="Modifier la fiche" data-testid={`edit-${c.id}`}><Edit3 className="h-3.5 w-3.5" /></Link>
                          <Link to={`/admin/members/${c.slug}/visuals`} className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-800 hover:bg-amber-100" data-testid={`canva-${c.id}`}><Sparkles className="h-3 w-3" />Mini-Canva</Link>
                          <VisualDownloader kind="member" slug={c.slug} />
                          {c.archived
                            ? <button onClick={() => unarchive(c)} className="grid h-7 w-7 place-items-center rounded-lg text-neutral-500 hover:bg-emerald-50 hover:text-emerald-700" title="Restaurer" data-testid={`unarchive-${c.id}`}><ArchiveRestore className="h-3.5 w-3.5" /></button>
                            : <button onClick={() => archive(c)} className="grid h-7 w-7 place-items-center rounded-lg text-neutral-500 hover:bg-amber-50 hover:text-amber-700" title="Archiver" data-testid={`archive-${c.id}`}><Archive className="h-3.5 w-3.5" /></button>}
                          <button onClick={() => remove(c)} className="grid h-7 w-7 place-items-center rounded-lg text-neutral-500 hover:bg-red-50 hover:text-red-700" title="Supprimer" data-testid={`delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {table.items.length === 0 && !table.loading && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-neutral-500">Aucun membre{table.q ? ` pour "${table.q}"` : ""}.</td></tr>
                )}
                {table.loading && <tr><td colSpan={7} className="px-4 py-10 text-center text-xs text-neutral-400">Chargement…</td></tr>}
              </tbody>
            </table>
          </div>
          <AdminTablePager table={table} />
        </div>
        {creating && <CreateMemberModal categories={categories} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); table.reload(); }} />}
        {dupOpen && (
          <CompanyDuplicatesModal
            groups={dupGroups || []}
            onClose={() => setDupOpen(false)}
            onMerge={mergeDuplicates}
          />
        )}
      </div>
    </PortalLayout>
  );
}

function CreateMemberModal({ categories, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", legal_name: "", siren: "", vat_number: "", city: "", country: "France", lang: langForCountry("France"), pole: "", category: "", status: "active", email: "", website: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.name) { toast.error("Nom requis"); return; }
    setSaving(true);
    try { await api.post("/admin/companies", form); toast.success("Membre créé"); onSaved(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-xl rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="create-member-modal">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-lg font-bold text-navy">Nouveau membre</h3>
          <button onClick={onClose} className="grid h-7 w-7 place-items-center rounded-full hover:bg-neutral-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="mb-3 rounded-xl border border-emerald-200 bg-emerald-50/40 p-3">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700">🔎 Recherche INSEE</div>
          <CompanyAutocomplete
            placeholder="Tapez nom ou SIREN…"
            onSelect={(r) => {
              if (!r) return;
              setForm((f) => ({
                ...f,
                name: r.denomination || f.name,
                legal_name: r.denomination || f.legal_name,
                siren: r.siren || f.siren,
                vat_number: r.vat_number || f.vat_number,
                city: r.city || f.city,
                country: r.country || f.country,
              }));
            }}
          />
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom commercial *" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="member-name" />
            <div className="mt-0.5 text-[10px] text-neutral-400">Affiché sur tous les supports publics (annuaire, événements, visuels, badges)</div>
          </div>
          <div>
            <input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} placeholder="Entité légale (facturation)" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="member-legal-name" />
            <div className="mt-0.5 text-[10px] text-neutral-400">Utilisée pour Stripe & factures</div>
          </div>
          <input value={form.siren} onChange={(e) => setForm({ ...form, siren: e.target.value })} placeholder="SIREN" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
          <input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} placeholder="N° TVA" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
          <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Ville" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
          <CountrySelect value={form.country} onSelect={({ name, lang }) => setForm({ ...form, country: name, lang })} testId="member-create-country" />
          <select value={form.pole} onChange={(e) => setForm({ ...form, pole: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm">
            <option value="">— Pôle —</option>
            {POLES.map((p) => <option key={p.id} value={p.id}>{p.emoji} {p.label}</option>)}
          </select>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm">
            <option value="">— Catégorie —</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm">
            <option value="active">Actif</option>
            <option value="pending">En attente paiement</option>
            <option value="inactive">Inactif</option>
          </select>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-neutral-300 px-3 py-1.5 text-xs font-bold">Annuler</button>
          <button onClick={save} disabled={saving || !form.name} className="rounded-full bg-wine-600 px-4 py-1.5 text-xs font-bold text-white disabled:opacity-50" data-testid="save-member-btn">{saving ? "…" : "Créer"}</button>
        </div>
      </div>
    </div>
  );
}


function CompanyDuplicatesModal({ groups, onClose, onMerge }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="company-duplicates-modal">
      <div className="relative max-h-[88vh] w-full max-w-3xl overflow-auto rounded-2xl bg-white p-6 shadow-xl">
        <button onClick={onClose} className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full hover:bg-neutral-100" aria-label="Fermer"><X className="h-4 w-4" /></button>
        <h2 className="text-xl font-bold text-navy"><Users2 className="mr-1 inline h-5 w-5 text-amber-600" /> Doublons d&apos;entreprises détectés</h2>
        <p className="mt-1 text-xs text-neutral-600">
          Les entreprises sont groupées par nom (insensible à la casse et aux espaces). Choisissez la fiche principale puis fusionnez — les inscriptions événement, contacts, news et candidatures seront rattachés à la principale, et les doublons archivés.
        </p>
        <div className="mt-5 space-y-4">
          {groups.length === 0 && (
            <div className="rounded-lg bg-emerald-50 p-4 text-center text-sm text-emerald-700" data-testid="no-duplicates">
              Aucun doublon détecté. 🎉
            </div>
          )}
          {groups.map((g, gi) => (
            <CompanyDuplicateGroup key={g.name + gi} group={g} onMerge={onMerge} gi={gi} />
          ))}
        </div>
      </div>
    </div>
  );
}

function CompanyDuplicateGroup({ group, onMerge, gi }) {
  const [primary, setPrimary] = useState(group.companies[0]?.id || "");
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4" data-testid={`company-dup-group-${gi}`}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-amber-900">
          {group.name} <span className="text-xs text-neutral-500">({group.count} fiches)</span>
        </div>
        <button
          onClick={() => onMerge(group, primary)}
          disabled={!primary}
          className="inline-flex items-center gap-1.5 rounded-full bg-amber-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-amber-700 disabled:opacity-50"
          data-testid={`merge-company-group-${gi}`}
        >
          <Users2 className="h-3 w-3" /> Fusionner
        </button>
      </div>
      <div className="mt-3 space-y-2">
        {group.companies.map((c) => (
          <label
            key={c.id}
            className={`flex cursor-pointer items-start justify-between gap-3 rounded-lg border bg-white px-3 py-2 text-xs ${primary === c.id ? "border-wine-500 ring-1 ring-wine-500" : "border-neutral-200"}`}
            data-testid={`company-dup-row-${c.id}`}
          >
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <input type="radio" name={`primary-${gi}`} checked={primary === c.id} onChange={() => setPrimary(c.id)} />
              <CompanyLogo src={c.logo} name={c.name} size={28} className="rounded shrink-0" />
              <div className="min-w-0">
                <div className="truncate font-semibold text-neutral-800">{c.name}</div>
                <div className="truncate text-[10px] text-neutral-500">
                  Statut: <span className={c.status === "active" ? "font-semibold text-emerald-700" : "text-neutral-600"}>{c.status || "—"}</span>
                  {c.city ? ` · ${c.city}` : ""}
                  {c.created_at ? ` · créé le ${new Date(c.created_at).toLocaleDateString("fr")}` : ""}
                  {c.siren ? ` · SIREN ${c.siren}` : ""}
                </div>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {primary === c.id && <span className="rounded-full bg-wine-100 px-2 py-0.5 text-[10px] font-bold text-wine-700">Principale</span>}
              <Link to={`/admin/companies/${c.id}`} target="_blank" rel="noreferrer" className="text-[10px] font-bold text-wine-600 hover:underline" onClick={(e) => e.stopPropagation()}>Ouvrir →</Link>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}


// Sprint 90 — Reusable bulk tag modal (add/remove multiple tags on selected companies)
function BulkTagModal({ ids, existingTags = [], onClose, onDone }) {
  const [selectedTags, setSelectedTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [action, setAction] = useState("add"); // "add" or "remove"
  const [saving, setSaving] = useState(false);

  const toggle = (t) => {
    setSelectedTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };
  const addFreeTag = () => {
    const s = newTag.trim().toLowerCase().replace(/\s+/g, "-");
    if (!s) return;
    if (!selectedTags.includes(s)) setSelectedTags([...selectedTags, s]);
    setNewTag("");
  };

  const submit = async () => {
    if (selectedTags.length === 0) { toast.error("Sélectionnez au moins un tag"); return; }
    setSaving(true);
    try {
      const r = await api.post("/admin/companies/bulk-tag", { ids, tags: selectedTags, action });
      toast.success(
        action === "add"
          ? `✅ ${r.data.modified} membre(s) tagué(s) avec : ${selectedTags.join(", ")}`
          : `🗑️ Tag(s) retiré(s) sur ${r.data.modified} membre(s)`,
      );
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="bulk-tag-modal">
        <div className="flex items-center justify-between border-b border-neutral-100 px-6 py-4">
          <div>
            <h3 className="text-xl font-bold text-navy"><TagIcon className="mr-1.5 inline h-5 w-5 text-indigo-500" /> Tags pour {ids.length} membre{ids.length > 1 ? "s" : ""}</h3>
            <p className="mt-0.5 text-xs text-neutral-500">Ex : <code>membre</code>, <code>oenotourisme</code>, <code>winetech-suite</code>, <code>vip</code>…</p>
          </div>
          <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-full hover:bg-neutral-100"><X className="h-4 w-4" /></button>
        </div>

        <div className="space-y-4 p-6">
          {/* Action toggle */}
          <div className="inline-flex rounded-full border border-neutral-200 bg-neutral-50 p-0.5 text-xs font-bold" data-testid="tag-action-toggle">
            <button onClick={() => setAction("add")} className={`rounded-full px-3 py-1 ${action === "add" ? "bg-emerald-500 text-white" : "text-neutral-500"}`} data-testid="tag-action-add">
              + Ajouter
            </button>
            <button onClick={() => setAction("remove")} className={`rounded-full px-3 py-1 ${action === "remove" ? "bg-rose-500 text-white" : "text-neutral-500"}`} data-testid="tag-action-remove">
              − Retirer
            </button>
          </div>

          {/* Free text input */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Nouveau tag (Entrée pour ajouter à la liste)</label>
            <div className="mt-1 flex gap-2">
              <input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFreeTag(); } }}
                placeholder="ex : oenotourisme"
                className="flex-1 rounded-lg border border-neutral-200 px-3 py-2 text-sm"
                data-testid="tag-new-input"
              />
              <button onClick={addFreeTag} className="rounded-full bg-neutral-100 px-3 py-2 text-xs font-bold text-neutral-700 hover:bg-neutral-200">
                <Plus className="inline h-3.5 w-3.5" /> Ajouter
              </button>
            </div>
          </div>

          {/* Existing tags */}
          {existingTags.length > 0 && (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Tags existants (cliquez pour sélectionner)</label>
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="existing-tags">
                {existingTags.map((t) => {
                  const on = selectedTags.includes(t);
                  return (
                    <button
                      key={t}
                      onClick={() => toggle(t)}
                      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold border transition ${on ? "border-indigo-500 bg-indigo-100 text-indigo-800" : "border-neutral-200 bg-white text-neutral-600 hover:border-indigo-300"}`}
                      data-testid={`existing-tag-${t}`}
                    >
                      {t}{on ? " ✓" : ""}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Selected tags */}
          {selectedTags.length > 0 && (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-2.5">
              <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-700">Tags sélectionnés ({selectedTags.length})</div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {selectedTags.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 rounded-full bg-indigo-500 px-2 py-0.5 text-[11px] font-bold text-white">
                    {t}
                    <button onClick={() => toggle(t)} className="hover:text-neutral-200"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button onClick={onClose} className="rounded-full border border-neutral-200 px-4 py-2 text-xs font-semibold text-neutral-600 hover:bg-neutral-50">Annuler</button>
            <button
              onClick={submit}
              disabled={saving || selectedTags.length === 0}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold text-white disabled:opacity-40 ${action === "add" ? "bg-emerald-500 hover:bg-emerald-600" : "bg-rose-500 hover:bg-rose-600"}`}
              data-testid="tag-submit"
            >
              {saving ? "…" : action === "add" ? "Appliquer les tags" : "Retirer les tags"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Sprint 92ak — Bulk update pole and/or category on selected members.
function BulkFieldsModal({ ids, categories = [], onClose, onDone }) {
  const [pole, setPole] = useState("");
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!pole && !category) {
      toast.error("Sélectionnez au moins un champ à modifier (pôle et/ou catégorie)");
      return;
    }
    const changes = [pole ? `pôle = ${poleLabel(pole)}` : "", category ? `catégorie = ${categories.find((c) => c.id === category)?.name || category}` : ""].filter(Boolean).join(" et ");
    if (!window.confirm(`Appliquer ${changes} à ${ids.length} membre${ids.length > 1 ? "s" : ""} ?`)) return;
    setBusy(true);
    try {
      const body = { ids };
      if (pole) body.pole = pole;
      if (category) body.category = category;
      const r = await api.post("/admin/companies/bulk-update-fields", body);
      toast.success(`✅ ${r.data.modified} membre(s) mis à jour (${changes})`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="bulk-fields-modal">
        <div className="flex items-center justify-between border-b border-neutral-100 px-5 py-4">
          <h3 className="text-base font-bold text-navy inline-flex items-center gap-2">
            <Shuffle className="h-4 w-4 text-wine-500" /> Modifier pôle / catégorie
          </h3>
          <button onClick={onClose} className="rounded-full p-1 hover:bg-neutral-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-4 p-5">
          <p className="text-xs text-neutral-600">Appliqué à <strong>{ids.length}</strong> membre{ids.length > 1 ? "s" : ""} sélectionné{ids.length > 1 ? "s" : ""}. Laissez vide pour ne pas toucher à un champ.</p>

          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-neutral-600">Pôle</span>
            <select
              value={pole}
              onChange={(e) => setPole(e.target.value)}
              className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
              data-testid="bulk-fields-pole"
            >
              <option value="">— Ne pas changer —</option>
              {POLES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </label>

          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-neutral-600">Catégorie d'adhésion</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
              data-testid="bulk-fields-category"
            >
              <option value="">— Ne pas changer —</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>

          <div className="flex justify-end gap-2 border-t border-neutral-100 pt-4">
            <button onClick={onClose} className="rounded-full border border-neutral-200 px-4 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50" data-testid="bulk-fields-cancel">Annuler</button>
            <button
              onClick={submit}
              disabled={busy || (!pole && !category)}
              className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-5 py-1.5 text-xs font-bold text-white hover:bg-wine-700 disabled:opacity-50"
              data-testid="bulk-fields-apply"
            >
              {busy ? "Application…" : "Appliquer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

